import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type { Attachment } from '@clawlet/gateway-client';
import ContextTokenWarningDialog from '../ContextTokenWarningDialog';
import { useAppStore } from '../../stores/appStore';
import {
  useSettingsStore,
  type ThemeColors,
  type ThemePreset,
} from '../../stores/settingsStore';
import {
  fileToBase64,
  fileToDataUrl,
  formatFileSize,
  isImageFile,
  prepareImageForUpload,
} from '../../utils/imageUtils';
import { toFriendlyErrorMessage } from '../../utils/friendlyErrors';
import { getSessionTokenCount, shouldShowContextTokenWarning } from '../../utils/contextTokenWarning';

interface ThemeBuilderChatProps {
  sendMessage: (text: string, attachments?: Attachment[]) => Promise<void>;
}

const REQUIRED_THEME_KEYS: (keyof ThemeColors)[] = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'primary',
  'primary-hover',
  'primary-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'destructive-border',
  'success',
  'warning',
  'border',
  'input',
  'ring',
  'ring-light',
  'primary-90',
  'primary-20',
  'ring-70',
  'scrollbar-thumb',
  'scrollbar-thumb-hover',
  'banner-bg',
  'user-bubble-background',
  'user-bubble-text',
  'assistant-bubble-background',
  'assistant-bubble-text',
];

const THEME_TOKEN_INSTRUCTIONS = `Generate a color theme for Clawlet. Return a JSON code block with this EXACT structure (all 32 keys required):

\`\`\`json
{
  "name": "Theme Name",
  "colors": {
    "background": "#hex",
    "foreground": "#hex",
    "card": "#hex",
    "card-foreground": "#hex",
    "secondary": "#hex",
    "secondary-foreground": "#hex",
    "muted": "#hex",
    "muted-foreground": "#hex",
    "primary": "#hex or rgba()",
    "primary-hover": "#hex",
    "primary-foreground": "#hex",
    "accent": "rgba(r, g, b, 0.2-0.5)",
    "accent-foreground": "#hex",
    "destructive": "rgba(r, g, b, 0.2-0.3)",
    "destructive-foreground": "#hex",
    "destructive-border": "#hex",
    "success": "#hex",
    "warning": "#hex",
    "border": "#hex",
    "input": "#hex",
    "ring": "#hex",
    "ring-light": "#hex",
    "primary-90": "rgba(r, g, b, 0.9)",
    "primary-20": "rgba(r, g, b, 0.2)",
    "ring-70": "rgba(r, g, b, 0.7)",
    "scrollbar-thumb": "#hex",
    "scrollbar-thumb-hover": "#hex",
    "banner-bg": "rgba(r, g, b, 0.9)",
    "user-bubble-background": "#hex",
    "user-bubble-text": "#hex",
    "assistant-bubble-background": "#hex",
    "assistant-bubble-text": "#hex"
  }
}
\`\`\`

Token meanings: background=main bg, foreground=primary text, card=sidebar/panel bg, secondary=elevated surfaces, muted=disabled/placeholder, primary=buttons/accent action color, accent=subtle highlight (needs rgba), destructive=error states (needs rgba), border/input=border colors, ring=focus ring, user-bubble-background/text=user chat message bubble colors (slightly muted from primary), assistant-bubble-background/text=assistant chat message bubble colors.
Rules: accent, destructive, primary-90, primary-20, ring-70, banner-bg MUST use rgba(). User bubble should be a muted/darker shade of primary. Ensure WCAG AA contrast. ALL 32 keys required.`;

function buildThemeRequestMessage(userText: string, hasImage: boolean): string {
  const userRequest = userText.trim() || (hasImage ? 'Create a theme inspired by this image.' : '');
  return `[Theme Builder] ${userRequest}\n\n${THEME_TOKEN_INSTRUCTIONS}`;
}

function extractThemeFromResponse(text: string): ThemePreset | null {
  const jsonMatch = text.match(/```json\s*([\s\S]*?)```/i);
  if (!jsonMatch) return null;

  try {
    const parsed = JSON.parse(jsonMatch[1]);
    if (!parsed.colors || typeof parsed.colors !== 'object') {
      return null;
    }

    for (const key of REQUIRED_THEME_KEYS) {
      if (typeof parsed.colors[key] !== 'string') {
        return null;
      }
    }

    return {
      id: 'temp',
      name: typeof parsed.name === 'string' && parsed.name.trim() ? parsed.name : 'AI Generated Theme',
      colors: parsed.colors as ThemeColors,
    };
  } catch {
    return null;
  }
}

function PalettePreview({ theme }: { theme: ThemePreset }) {
  const swatches = [
    theme.colors.background,
    theme.colors.card,
    theme.colors.primary,
    theme.colors['accent-foreground'],
    theme.colors.foreground,
  ];

  return (
    <div className="mt-2">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Palette Preview</div>
      <div className="flex h-6 gap-1 overflow-hidden rounded-md border border-border">
        {swatches.map((color, index) => (
          <div key={`${color}-${index}`} className="h-full flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>

      <ContextTokenWarningDialog
        tokenCount={pendingTokenWarningCount}
        onCancel={clearTokenWarning}
        onProceed={handleProceedTokenWarning}
      />
    </div>
  );
}

export default function ThemeBuilderChat({ sendMessage }: ThemeBuilderChatProps) {
  const setTempTheme = useSettingsStore((s) => s.setTempTheme);
  const activeSessionKey = useAppStore((s) => s.activeSessionKey);
  const sessions = useAppStore((s) => s.sessions);
  const sessionMessages = useAppStore((s) =>
    s.activeSessionKey ? (s.messages[s.activeSessionKey] ?? []) : [],
  );

  const [input, setInput] = useState('');
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [compressionSummary, setCompressionSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ theme: ThemePreset; text: string } | null>(null);
  const [pendingTokenWarningText, setPendingTokenWarningText] = useState<string | null>(null);
  const [pendingTokenWarningCount, setPendingTokenWarningCount] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const sentRef = useRef<{ sessionKey: string | null; messageCountBefore: number } | null>(null);

  const canSend = useMemo(() => {
    return !loading && Boolean(input.trim() || pendingFile);
  }, [input, loading, pendingFile]);

  const handleImageSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      event.target.value = '';
      return;
    }

    if (!isImageFile(file)) {
      setError("This file type isn't supported.");
      event.target.value = '';
      return;
    }

    event.target.value = '';

    try {
      const processed = await prepareImageForUpload(file);
      const preview = await fileToDataUrl(processed.file);
      setPendingImage(preview);
      setPendingFile(processed.file);
      setCompressionSummary(
        processed.wasCompressed
          ? `${formatFileSize(processed.originalSizeBytes)} → ${formatFileSize(processed.finalSizeBytes)}`
          : null,
      );
      setError(null);
    } catch (imageError) {
      console.error('[ThemeBuilderChat] Failed to prepare image for upload:', imageError);
      setError(toFriendlyErrorMessage(imageError, 'theme-image'));
      setCompressionSummary(null);
    }
  };

  const clearTokenWarning = () => {
    setPendingTokenWarningText(null);
    setPendingTokenWarningCount(null);
  };

  useEffect(() => {
    clearTokenWarning();
  }, [activeSessionKey]);

  const handleSend = async (skipTokenWarning = false) => {
    const draftText = input;
    const text = draftText.trim();
    const hasImage = Boolean(pendingFile);

    if (!text && !hasImage) return;
    if (loading) return;

    if (!skipTokenWarning) {
      const tokenCount = getSessionTokenCount(sessions, activeSessionKey);
      if (shouldShowContextTokenWarning({ messageText: draftText, hasAttachments: hasImage, tokenCount })) {
        setPendingTokenWarningText(draftText);
        setPendingTokenWarningCount(tokenCount);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let attachments: Attachment[] | undefined;
      if (pendingFile) {
        const base64 = await fileToBase64(pendingFile);
        attachments = [
          {
            mimeType: pendingFile.type || 'image/png',
            fileName: pendingFile.name,
            content: base64,
          },
        ];
      }

      const state = useAppStore.getState();
      const currentSessionKey = state.activeSessionKey;
      const currentMessages = currentSessionKey ? (state.messages[currentSessionKey] ?? []) : [];
      const messageCountBefore = currentMessages.length;
      sentRef.current = { sessionKey: currentSessionKey, messageCountBefore };

      const message = buildThemeRequestMessage(text, hasImage);
      await sendMessage(message, attachments);

      setInput('');
      setPendingImage(null);
      setPendingFile(null);
      setCompressionSummary(null);
    } catch (sendError) {
      console.error('[ThemeBuilderChat] Failed to send theme request:', sendError);
      setError(toFriendlyErrorMessage(sendError, 'theme-request'));
      setLoading(false);
      sentRef.current = null;
    }
  };

  const handleProceedTokenWarning = () => {
    if (pendingTokenWarningText === null) {
      return;
    }

    clearTokenWarning();
    void handleSend(true);
  };

  useEffect(() => {
    if (!loading || !sentRef.current) return;

    const { sessionKey, messageCountBefore } = sentRef.current;
    if (sessionKey !== activeSessionKey) return;

    const newMessages = sessionMessages.slice(messageCountBefore);
    const assistantMsg = newMessages.find((message) => message.role === 'assistant');

    if (!assistantMsg) {
      return;
    }

    const extractedTheme = extractThemeFromResponse(assistantMsg.content);
    const extractedThemeRetry = extractedTheme ?? extractThemeFromResponse(assistantMsg.content.trim());

    if (extractedThemeRetry) {
      setTempTheme(extractedThemeRetry);
      setResult({ theme: extractedThemeRetry, text: assistantMsg.content });
      setError(null);
    } else {
      setError('The agent responded but no valid theme was found. Try a more specific description.');
    }

    setLoading(false);
    sentRef.current = null;
  }, [loading, activeSessionKey, sessionMessages, setTempTheme]);

  useEffect(() => {
    if (!loading || !sentRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setError('Theme generation timed out. Please try again.');
      setLoading(false);
      sentRef.current = null;
    }, 60_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loading]);

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-sm font-medium text-card-foreground">🎨 AI Theme Builder</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Describe a theme or upload a screenshot. The assistant will generate a complete token-based
        theme you can preview instantly.
      </p>

      {!result && !loading && !error && (
        <div className="mt-3 rounded-lg border border-input bg-background/70 px-3 py-3 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Try prompts like:</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            <li>Create a warm sunset theme with soft contrast</li>
            <li>Make a cyberpunk neon theme inspired by Tokyo nightlife</li>
            <li>Match this screenshot with accessible contrast</li>
          </ul>
        </div>
      )}

      {pendingImage && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-input bg-background/80 p-2">
          <img
            src={pendingImage}
            alt="Pending upload"
            className="h-16 w-16 rounded-md border border-border object-cover"
          />
          <div className="flex-1">
            <p className="text-xs text-foreground">Image ready</p>
            <p className="text-xs text-muted-foreground">
              {compressionSummary ? `Compressed ${compressionSummary}` : 'Will be sent with your request'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setPendingImage(null);
              setPendingFile(null);
              setCompressionSummary(null);
            }}
            className="rounded-md border border-input px-2 py-1 text-xs text-card-foreground transition-colors hover:border-muted-foreground"
            aria-label="Remove pending image"
          >
            ×
          </button>
        </div>
      )}

      <form
        className="mt-3 flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSend();
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="rounded-lg border border-input bg-background px-2.5 py-2 text-xs text-card-foreground transition-colors hover:border-muted-foreground"
          title="Upload screenshot"
          disabled={loading}
        >
          📷 Image
        </button>

        <input
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Describe your ideal theme..."
          className="h-9 flex-1 rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-ring focus:ring-1 focus:ring-ring"
          disabled={loading}
        />

        <button
          type="submit"
          disabled={!canSend}
          className="rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          Send
        </button>
      </form>

      {loading && (
        <p className="mt-3 text-xs text-secondary-foreground">
          <span className="animate-pulse">Generating your theme...</span>
        </p>
      )}

      {error && <p className="mt-3 text-xs text-destructive-foreground">{error}</p>}

      {result && (
        <div className="mt-3 rounded-lg border border-input bg-background/70 p-3">
          <p className="text-sm font-medium text-foreground">Generated: {result.theme.name}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Theme extracted and applied. Save it below if you want to keep it.
          </p>
          <PalettePreview theme={result.theme} />
        </div>
      )}
    </div>
  );
}
